import React, { ChangeEvent, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { DashedBox } from "src/app/components/DashedBox/DashedBox";
import { ReactComponent as LinkIcon } from "src/images/link-alt.svg";
import { ReactComponent as UploadIcon } from "src/images/upload-file.svg";
import { ReactComponent as DownloadIcon } from "src/images/download-rounded.svg";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import FileUploader from "src/app/components/FileUploader/FileUploader";
import AddLinksModal from "src/app/modals/AddLinksModal/AddLinksModal";
import { PublicationsService } from "src/services";
import { SectionHeader } from "src/app/components/Pane/SectionHeader/SectionHeader";
import { Button } from "src/app/components/Button/Button";
import { RemoveModal } from "src/app/modals/RemoveModal/RemoveModal";
import {
  deletePublishedContentByUuid,
  getTaskDetails,
} from "src/redux/task/taskActions";
import { PublishedContent } from "src/types";
import UploadAttachments from "src/app/libs/uploadAttachments";
import { MAX_SIZE_SINGLE_ATTACHMENT } from "src/utils/uploadFunctionsUtils";
import { RootState } from "src/redux/reducers";
import useCheckProjectPermission from "src/hooks/useCheckProjectPermission";
import { EmptyTabContainer } from "src/app/Task/tabs/ProfileDataTab/components/EmptyTabs/EmptyTabContainer/EmptyTabContainer";
import { EmptyTabContent } from "src/app/Task/tabs/ProfileDataTab/components/EmptyTabs/EmptyTabContent/EmptyTabContent";
import emptyPublishedContentImg from "src/images/empty-content-proposal-client.svg";
import ContentProposalDownloadModal from "src/app/modals/ContentDownloadModal/ContentProposalDownloadModal";
import CustomError from "src/app/libs/customError";
import { handleError } from "src/utils/error";
import PublishedContentListItem from "./PublishedContentListItem";
import SkeletonAttachment from "./SkeletonAttachment";

import "./PublishedContent.scss";

const SUPPORTED_FILE_TYPES = ["jpg", "jpeg", "png", "mov", "mp4", "zip"];

const acceptedFileTypes = SUPPORTED_FILE_TYPES.map((type) => `.${type}`).join(
  ",",
);

interface Props {
  taskUuid: string;
  publishedContents: PublishedContent[] | null;
}

export default function PublishedContentTab({
  taskUuid,
  publishedContents,
}: Props) {
  const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState(false);
  const [value, setValue] = useState("");
  const [deletedContentData, setDeletedContentData] =
    useState<PublishedContent | null>(null);
  const [showContentDownloadModal, setShowContentDownloadModal] =
    useState(false);

  const [isDownloadingAllContent, setIsDownloadingAllContent] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<
    { id: string; name: string }[]
  >([]);

  const hasTaskManagementPermission =
    useCheckProjectPermission("TASK_MANAGEMENT");

  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const dispatch = useDispatch();

  const handleAddLinks = async (links: string[]) => {
    try {
      await PublicationsService.createContent({
        wsPublicationTaskUuid: taskUuid,
        attachments: links.map((link) => ({ externalUrl: link })),
      });
      dispatch(getTaskDetails(taskUuid));
    } catch (error) {
      handleError(error);
    }
  };

  const handleRemoveLink = async (uuid: string) => {
    await dispatch(deletePublishedContentByUuid(uuid));
    setDeletedContentData(null);
  };

  const handleOpenFileUploader = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadContent = async () => {
    setIsDownloadingAllContent(true);
    setShowContentDownloadModal(true);
    try {
      await PublicationsService.downloadContent([taskUuid]);
    } catch (error) {
      handleError(error);
    } finally {
      setIsDownloadingAllContent(false);
    }
  };

  const handleChangeInputFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files === null) {
      throw new CustomError({
        id: "ws_no_file_selected",
        defaultMessage: "No file selected",
      });
    }

    const filesArray = Array.from(files);

    const uploadingFilesData = filesArray.map((file, index) => ({
      id: `upload-${Date.now()}-${index}`,
      name: file.name,
    }));
    setUploadingFiles(uploadingFilesData);

    try {
      const uploadAttachments = new UploadAttachments({
        files: filesArray,
        allowedTypes: SUPPORTED_FILE_TYPES,
        maxSize: MAX_SIZE_SINGLE_ATTACHMENT,
      });

      uploadAttachments.validate();

      if (filesArray.some((file) => file.type.length === 0)) {
        throw new CustomError({
          id: "ws_invalid_file_type",
          defaultMessage: "Invalid file type",
        });
      }

      const filesData = Array.from({ length: filesArray.length }, () => ({
        wsPublicationTaskUuid: taskUuid,
        wsWorkspaceUuid: activeWorkspaceUuid,
      }));

      const presignedRequestResponse =
        await PublicationsService.createPresignedRequestForContent(filesData);

      await uploadAttachments.sendAttachmentsToS3(
        presignedRequestResponse.content,
      );

      const attachmensToCreate = filesArray.map((file, index) => {
        const { requestInputs } = presignedRequestResponse.content[index].token;

        const path = requestInputs.key.split("/");
        const realName = path[path.length - 1];
        return {
          key: presignedRequestResponse.content[index].token.requestInputs.key,
          name: file.name,
          realName,
          mimeType: file.type,
          size: file.size,
        };
      });

      await PublicationsService.createContent({
        wsPublicationTaskUuid: taskUuid,
        attachments: attachmensToCreate,
      });
      dispatch(getTaskDetails(taskUuid));
    } catch (error) {
      handleError(error);
    } finally {
      setUploadingFiles([]);
      if (fileInputRef?.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const renderContent = () => {
    if (
      (publishedContents === null || publishedContents.length === 0) &&
      uploadingFiles.length === 0
    ) {
      if (hasTaskManagementPermission) {
        return (
          <div className="published-content__upload-tiles">
            <FileUploader
              maxFileSizeInMB={MAX_SIZE_SINGLE_ATTACHMENT}
              supportedFormats={SUPPORTED_FILE_TYPES}
              handleOpenFileUploader={handleOpenFileUploader}
            />
            <DashedBox
              className="file-uploader__content"
              onClick={() => setIsAddLinkModalOpen(true)}
            >
              <div className="file-uploader__cta">
                <LinkIcon />
                <IDHFormattedMessage
                  id="ws_paste_link"
                  defaultMessage="Paste link"
                />
              </div>
            </DashedBox>
          </div>
        );
      }
      return (
        <EmptyTabContainer>
          <EmptyTabContent
            header={
              <IDHFormattedMessage
                id="ws_empty_published_content_header"
                defaultMessage="There is no published content yet"
              />
            }
            text={
              <IDHFormattedMessage
                id="ws_empty_published_content_text"
                defaultMessage="Please wait patiently for it to appear."
              />
            }
            imgUrl={emptyPublishedContentImg}
          />
        </EmptyTabContainer>
      );
    }
    return (
      <div className="published-content__list-wrapper">
        <div className="published-content__header">
          <SectionHeader
            name={
              <IDHFormattedMessage
                id="ws_published_content"
                defaultMessage="Published content"
              />
            }
          />

          <div className="published-content__actions">
            {hasTaskManagementPermission && (
              <>
                <Button
                  variant="white"
                  size="small"
                  textWithIcon
                  onClick={handleOpenFileUploader}
                >
                  <UploadIcon />
                  <IDHFormattedMessage
                    id="ws_upload_file"
                    defaultMessage="Upload file"
                  />
                </Button>
                <Button
                  variant="white"
                  size="small"
                  textWithIcon
                  onClick={() => setIsAddLinkModalOpen(true)}
                >
                  <LinkIcon />
                  <IDHFormattedMessage
                    id="ws_paste_link"
                    defaultMessage="Paste link"
                  />
                </Button>{" "}
              </>
            )}
            <Button
              variant="white"
              size="small"
              textWithIcon
              onClick={handleDownloadContent}
              disabled={isDownloadingAllContent}
            >
              <DownloadIcon />
              <IDHFormattedMessage
                id="ws_download_all"
                defaultMessage="Download all"
              />
            </Button>
          </div>
        </div>

        <ul className="published-content__list">
          {uploadingFiles.map((file) => (
            <li key={file.id}>
              <SkeletonAttachment fileName={file.name} />
            </li>
          ))}
          {publishedContents?.map((item) => (
            <PublishedContentListItem
              key={item.uuid}
              data={item}
              onClickRemoveContent={
                hasTaskManagementPermission
                  ? () => setDeletedContentData(item)
                  : undefined
              }
            />
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="published-content">
      {renderContent()}
      {isAddLinkModalOpen && (
        <AddLinksModal
          onClose={() => setIsAddLinkModalOpen(false)}
          onConfirmClick={handleAddLinks}
          value={value}
          setValue={setValue}
        />
      )}
      {deletedContentData && (
        <RemoveModal
          onClose={() => setDeletedContentData(null)}
          objectNames={[deletedContentData.name]}
          removeFunction={() => handleRemoveLink(deletedContentData.uuid)}
        />
      )}
      {showContentDownloadModal && (
        <ContentProposalDownloadModal
          onClose={() => {
            setShowContentDownloadModal(false);
          }}
        />
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleChangeInputFiles}
        accept={acceptedFileTypes}
      />
    </div>
  );
}
